/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import {
  collection, query, where, onSnapshot, orderBy,
  limit, getDocs, doc, getDoc, setDoc
} from 'firebase/firestore';
import { Avatar } from './ui';
import { ESTADOS_LECTURA, LISTAS_LIBROS, generarIdLibro } from './constants';
import { RetoLector } from './RetoLector';
import { WrappedAnual, WrapMensual } from './WrappedAnual';
import { BuscarPorVibe } from './MoodTags';

const ESTILOS_MARCO = {
  verde:   { border: '2px solid #1DB954', boxShadow: '0 0 0 1px #1DB95444' },
  dorado:  { border: '2px solid transparent', backgroundImage: 'linear-gradient(#0d0d0d, #0d0d0d), linear-gradient(135deg, #7d5a00, #FFD700, #FFA500, #7d5a00)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', boxShadow: '0 0 8px #FFD70066' },
  platino: { border: '2px solid transparent', backgroundImage: 'linear-gradient(#0d0d0d, #0d0d0d), linear-gradient(135deg, #1a0533, #a78bfa, #38bdf8, #1a0533)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', boxShadow: '0 0 8px #a78bfa66' },
  fuego:   { border: '2px solid transparent', backgroundImage: 'linear-gradient(#0d0d0d, #0d0d0d), linear-gradient(135deg, #7d1a00, #f44336, #ff9800, #f44336)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', boxShadow: '0 0 8px #f4433666' },
  oceano:  { border: '2px solid transparent', backgroundImage: 'linear-gradient(#0d0d0d, #0d0d0d), linear-gradient(135deg, #001a33, #0288d1, #00bcd4, #0288d1)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', boxShadow: '0 0 8px #0288d166' },
  aurora:  { border: '2px solid transparent', backgroundImage: 'linear-gradient(#0d0d0d, #0d0d0d), linear-gradient(135deg, #1a0533, #e91e63, #9c27b0, #3f51b5)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', boxShadow: '0 0 8px #e91e6366' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const dias = Math.floor(diff / 86400000);
  if (m < 1) return 'ahora';
  if (m < 60) return `${m}m`;
  if (h < 24) return `${h}h`;
  return `${dias}d`;
}

function fmtEvento(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function restante(ts) {
  if (!ts) return null;
  const diff = (ts.toDate ? ts.toDate().getTime() : ts) - Date.now();
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 48) return `en ${Math.floor(h / 24)} días`;
  if (h > 0) return `en ${h}h`;
  return `en ${m}min`;
}

// ── Tendencias (carrusel horizontal) ─────────────────────────────────────────
function SeccionTitulo({ texto, accion, onAccion }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '16px' }}>
      <h2 style={{ fontSize: '22px', fontWeight: 900, margin: 0, color: 'white', letterSpacing: '-0.3px' }}>{texto}</h2>
      {accion && (
        <button onClick={onAccion} style={{ background: 'none', border: 'none', color: '#555', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold', padding: 0, textTransform: 'uppercase', letterSpacing: '1px', transition: '0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'white'}
          onMouseLeave={e => e.currentTarget.style.color = '#555'}>
          Ver todo
        </button>
      )}
    </div>
  );
}

// ── Descubrimiento por similitud ──────────────────────────────────────────────
function DescubrimientoSimilitud({ userId, onVerLibro, onEntrarSala }) {
  const [libros, setLibros] = useState([]);

  useEffect(() => {
    if (!userId) return;
    let activo = true;

    const calcular = async () => {
      try {
        // 1. Mis libros leídos
        const misLibrosSnap = await getDocs(
          query(collection(db, 'estados_lectura'), where('userId', '==', userId), where('estado', '==', 'leido'))
        );
        const misLibrosIds = new Set(misLibrosSnap.docs.map(d => d.data().libroId).filter(Boolean));
        if (misLibrosIds.size < 2) return; // necesito al menos 2 libros para recomendar

        // 2. Usuarios que leyeron los mismos libros (máx 5 libros míos para no disparar demasiadas queries)
        const misLibrosMuestra = [...misLibrosIds].slice(0, 5);
        const usuariosSnap = await getDocs(
          query(collection(db, 'estados_lectura'),
            where('libroId', 'in', misLibrosMuestra),
            where('estado', '==', 'leido'),
            where('userId', '!=', userId)
          )
        );

        // 3. Contar qué libros leyeron esos usuarios (excluyendo los míos)
        const usuariosIds = new Set(usuariosSnap.docs.map(d => d.data().userId));
        if (usuariosIds.size === 0) return;

        const otrosLibrosSnap = await getDocs(
          query(collection(db, 'estados_lectura'),
            where('userId', 'in', [...usuariosIds].slice(0, 10)),
            where('estado', '==', 'leido')
          )
        );

        const conteo = {};
        const datosLibro = {};
        otrosLibrosSnap.docs.forEach(d => {
          const data = d.data();
          if (!data.libroId || misLibrosIds.has(data.libroId) || !data.libro?.titulo) return;
          conteo[data.libroId] = (conteo[data.libroId] || 0) + 1;
          if (!datosLibro[data.libroId]) datosLibro[data.libroId] = data.libro;
        });

        // 4. Top libros más leídos por usuarios similares
        const candidatos = Object.entries(conteo)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([libroId]) => ({ id: libroId, ...datosLibro[libroId] }))
          .filter(l => l.portada);

        if (!activo) return;
        setLibros(candidatos);
      } catch { }
    };

    calcular();
    return () => { activo = false; };
  }, [userId]);

  if (libros.length === 0) return null;

  return (
    <div className="feed-section">
      <SeccionTitulo texto="Basado en tus lecturas" />
      <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
        {libros.map(libro => (
          <div key={libro.id} style={{ flexShrink: 0, width: '110px', cursor: 'pointer' }} onClick={() => onVerLibro(libro)}>
            <div style={{ width: '110px', height: '160px', borderRadius: '8px', overflow: 'hidden', marginBottom: '6px', background: '#141414' }}>
              <img src={libro.portada} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={e => e.currentTarget.style.display = 'none'} />
            </div>
            <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 'bold', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{libro.titulo}</p>
            <p style={{ margin: 0, fontSize: '11px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{libro.autor}</p>
            {onEntrarSala && (
              <button onClick={e => { e.stopPropagation(); onEntrarSala(libro); }}
                style={{ marginTop: '6px', background: 'transparent', border: '1px solid #2a2a2a', color: '#aaa', borderRadius: '20px', padding: '4px 10px', cursor: 'pointer', fontSize: '10px', width: '100%', transition: '0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#1DB954'; e.currentTarget.style.color = 'white'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#aaa'; }}>
                Ver sala
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const GBOOKS_KEY = 'AIzaSyDmTfy3ePtPB-LUZjMkaXQdOU2CmSGT5cU';
let ultimaPeticionGBooks = 0;
const DELAY_GBOOKS = 1200;

async function leerCacheFirestore(titulo, autor) {
  const libroId = generarIdLibro(titulo, autor);
  try {
    const snap = await getDoc(doc(db, 'portadas_cache', libroId));
    if (snap.exists()) return snap.data();
  } catch { }
  return null;
}

async function buscarPortadaConCache(titulo, autor) {
  const libroId = generarIdLibro(titulo, autor);
  // 1. Intentar leer de Firestore (caché permanente)
  const cached = await leerCacheFirestore(titulo, autor);
  if (cached) return cached;

  // 2. Respetar rate limit de Google Books
  const ahora = Date.now();
  const espera = DELAY_GBOOKS - (ahora - ultimaPeticionGBooks);
  if (espera > 0) await new Promise(r => setTimeout(r, espera));
  ultimaPeticionGBooks = Date.now();

  // 3. Llamar a Google Books
  try {
    const q = encodeURIComponent(`intitle:${titulo} inauthor:${autor}`);
    const resp = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${q}&maxResults=5&key=${GBOOKS_KEY}`);
    if (!resp.ok) return null;
    const data = await resp.json();
    const item = (data.items || []).find(i => i.volumeInfo?.imageLinks?.thumbnail);
    if (!item) return null;
    const portada = (item.volumeInfo.imageLinks.thumbnail || '').replace('http://', 'https://');
    const descripcion = item.volumeInfo?.description?.slice(0, 200) || '';
    const resultado = { id: libroId, titulo, autor, portada, descripcion };
    // 4. Guardar en Firestore para siempre
    setDoc(doc(db, 'portadas_cache', libroId), resultado).catch(() => {});
    return resultado;
  } catch { return null; }
}

function TendenciasCarrusel({ onVerLibro, onEntrarSala }) {
  const [libros, setLibros] = useState([]);

  useEffect(() => {
    const buscar = async () => {
      const lista = LISTAS_LIBROS.Tendencias;
      // Leer caché en paralelo
      const resultados = await Promise.all(
        lista.map(libro => leerCacheFirestore(libro.titulo, libro.autor))
      );
      const conPortada = resultados.filter(Boolean).slice(0, 10);
      // Si faltan, completar con Google Books
      if (conPortada.length < 5) {
        for (let i = 0; i < lista.length && conPortada.length < 10; i++) {
          if (conPortada.find(l => l.titulo === lista[i].titulo)) continue;
          const datos = await buscarPortadaConCache(lista[i].titulo, lista[i].autor);
          if (datos?.portada) conPortada.push(datos);
        }
      }
      setLibros(conPortada);
    };
    buscar();
  }, []);

  if (!libros.length) return (
    <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ flexShrink: 0, width: '130px' }}>
          <div style={{ width: '130px', aspectRatio: '2/3', background: '#141414', borderRadius: '6px', animation: 'pulse 1.5s infinite' }} />
          <div style={{ height: '12px', background: '#141414', borderRadius: '4px', marginTop: '8px', animation: 'pulse 1.5s infinite' }} />
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
      {libros.map((libro, i) => (
        <div key={i} style={{ flexShrink: 0, width: window.innerWidth < 768 ? '110px' : '130px' }}>
          <div className="book-card" style={{ padding: 0, marginBottom: '8px' }}
            onClick={() => onVerLibro(libro)}>
            <img src={libro.portada} alt=""
              style={{ width: '100%', aspectRatio: '2/3', objectFit: 'cover', borderRadius: '6px', display: 'block' }} />
          </div>
          <p style={{ margin: '0 0 2px', fontWeight: 'bold', fontSize: '12px', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{libro.titulo}</p>
          <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{libro.autor}</p>
          <button onClick={() => onEntrarSala(libro, false)}
            style={{ background: 'transparent', color: '#1DB954', border: '1px solid #1DB95444', borderRadius: '12px', padding: '4px 10px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s', width: '100%' }}
            onMouseEnter={e => e.currentTarget.style.background = '#1DB95418'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            Ver sala
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Tarjeta de evento ─────────────────────────────────────────────────────────
function EventoRow({ evento, userId, onEntrar }) {
  const ahora = Date.now();
  const inicio = evento.fechaInicio?.toDate?.().getTime() || 0;
  const enVivo = inicio <= ahora;
  const tiempo = restante(evento.fechaInicio);
  const apuntados = evento.apuntados || [];
  const estaApuntado = apuntados.includes(userId);
  const [hover, setHover] = useState(false);

  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px', borderRadius: '8px', background: hover ? '#1a1a1a' : '#141414', border: enVivo ? '1px solid #1DB95433' : '1px solid #1e1e1e', marginBottom: '8px', transition: '0.15s' }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <img src={evento.libroPortada} style={{ width: '44px', height: '62px', objectFit: 'cover', borderRadius: '4px', display: 'block', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }} alt="" />
        {enVivo && <div style={{ position: 'absolute', bottom: 2, left: 0, right: 0, textAlign: 'center', background: '#f44336', borderRadius: '3px', fontSize: '8px', fontWeight: 900, color: 'white', padding: '1px 0' }}>VIVO</div>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: '0 0 2px', fontWeight: 900, fontSize: '14px', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{evento.titulo}</p>
        <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#555' }}>{evento.libroAutor}</p>
        <p style={{ margin: 0, fontSize: '11px', color: '#333' }}>{enVivo ? <span style={{ color: '#f44336', fontWeight: 'bold' }}>En curso · </span> : null}{fmtEvento(evento.fechaInicio)} · {apuntados.length}/{evento.maxParticipantes}</p>
      </div>
      {enVivo ? (
        <button onClick={() => onEntrar(evento)} style={{ background: '#1DB954', color: 'black', border: 'none', borderRadius: '20px', padding: '8px 16px', fontWeight: 900, cursor: 'pointer', fontSize: '12px', flexShrink: 0, transition: '0.2s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#1ed760'}
          onMouseLeave={e => e.currentTarget.style.background = '#1DB954'}>
          Entrar
        </button>
      ) : tiempo ? (
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#ff9800', fontWeight: 'bold' }}>{tiempo}</p>
          <button onClick={() => onEntrar(evento)} style={{ background: 'transparent', color: estaApuntado ? '#1DB954' : '#888', border: `1px solid ${estaApuntado ? '#1DB954' : '#333'}`, borderRadius: '20px', padding: '5px 12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', transition: '0.2s' }}>
            {estaApuntado ? '✓ Apuntado' : 'Apuntarme'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── Tarjeta de lector similar ─────────────────────────────────────────────────
function LectorRow({ usuario, onVer, onSeguir, yaSigo }) {
  const [hover, setHover] = useState(false);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px', borderRadius: '8px', background: hover ? '#1a1a1a' : '#141414', border: '1px solid #1e1e1e', marginBottom: '8px', transition: '0.15s' }}>
      <Avatar src={usuario.foto} nombre={usuario.nombre} size={44} onClick={() => onVer(usuario)} />
      <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onVer(usuario)}>
        <p style={{ margin: '0 0 2px', fontWeight: 'bold', fontSize: '14px', color: 'white' }}>{usuario.nombre}</p>
        <p style={{ margin: 0, fontSize: '12px', color: '#555' }}>{usuario.generos?.slice(0, 3).join(' · ') || 'Lector'}</p>
      </div>
      <button onClick={() => onSeguir(usuario)}
        style={{ background: yaSigo ? 'transparent' : '#1DB954', color: yaSigo ? '#555' : 'black', border: yaSigo ? '1px solid #333' : 'none', borderRadius: '20px', padding: '8px 18px', fontWeight: 900, cursor: 'pointer', fontSize: '12px', flexShrink: 0, transition: '0.2s' }}>
        {yaSigo ? 'Siguiendo' : 'Seguir'}
      </button>
    </div>
  );
}

// ── Tarjeta de actividad ──────────────────────────────────────────────────────
function ActividadRow({ item, onVerLibro, onVerPerfil }) {
  const estadoDef = ESTADOS_LECTURA.find(e => e.valor === item.estado);
  const [hover, setHover] = useState(false);
  const esResena = item.tipo === 'resena';
  const esProgreso = item.tipo === 'progreso';

  const textoAccion = () => {
    if (esResena) return <span style={{ color: '#555' }}>· valoró <span style={{ color: '#FFD700' }}>{'★'.repeat(item.estrellas)}</span></span>;
    if (esProgreso) {
      const pct = item.totalPaginas && item.paginaActual ? Math.round((item.paginaActual / item.totalPaginas) * 100) : null;
      return <span style={{ color: '#555' }}>· va por la <span style={{ color: '#1DB954', fontWeight: 'bold' }}>pág. {item.paginaActual}</span>{pct !== null ? <span style={{ color: '#333' }}> ({pct}%)</span> : ''}</span>;
    }
    if (estadoDef) return <span style={{ color: '#555' }}>· <span style={{ color: estadoDef.color === '#888' ? '#aaa' : estadoDef.color }}>{estadoDef.etiqueta}</span></span>;
    return <span style={{ color: '#555' }}>· visitó</span>;
  };

  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '8px 10px', borderRadius: '6px', background: hover ? '#1a1a1a' : 'transparent', transition: '0.15s', marginBottom: '2px' }}>
      <div style={{ flexShrink: 0, borderRadius: '50%', boxSizing: 'border-box', ...(item.usuarioMarco && ESTILOS_MARCO[item.usuarioMarco] ? { ...ESTILOS_MARCO[item.usuarioMarco], padding: '2px' } : {}) }}>
        <Avatar src={item.usuarioFoto} nombre={item.usuarioNombre} size={36} onClick={() => onVerPerfil(item)} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#aaa', lineHeight: '1.4' }}>
          <b style={{ color: 'white', cursor: 'pointer' }} onClick={() => onVerPerfil(item)}>{item.usuarioNombre}</b>
          {' '}{textoAccion()}
        </p>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }} onClick={() => onVerLibro(item)}>
          {item.portada && <img src={item.portada} style={{ width: '26px', height: '38px', objectFit: 'cover', borderRadius: '3px', flexShrink: 0 }} alt="" />}
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: 'bold', color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.titulo}</p>
            <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#444' }}>{item.autor}</p>
            {esResena && item.reseña && (
              <p style={{ margin: 0, fontSize: '12px', color: '#666', fontStyle: 'italic', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                "{item.reseña}"
              </p>
            )}
            {esProgreso && item.totalPaginas && (
              <div style={{ height: '3px', background: '#1a1a1a', borderRadius: '3px', overflow: 'hidden', width: '80px', marginTop: '4px' }}>
                <div style={{ height: '100%', width: `${Math.min(Math.round((item.paginaActual / item.totalPaginas) * 100), 100)}%`, background: '#1DB954', borderRadius: '3px' }} />
              </div>
            )}
          </div>
        </div>
      </div>
      <span style={{ fontSize: '11px', color: '#333', flexShrink: 0 }}>{fmt(item.fecha)}</span>
    </div>
  );
}

// ── Tarjeta sala activa ───────────────────────────────────────────────────────
function SalaCard({ sala, libroInfo, onEntrar }) {
  if (!libroInfo) return null;
  return (
    <div onClick={() => onEntrar(libroInfo)}
      style={{ background: '#141414', borderRadius: '12px', overflow: 'hidden', border: '1px solid #1e1e1e', marginBottom: '10px', display: 'flex', cursor: 'pointer', transition: '0.2s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.background = '#181818'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e1e1e'; e.currentTarget.style.background = '#141414'; }}>
      <img src={libroInfo.portada} style={{ width: '56px', objectFit: 'cover', flexShrink: 0, display: 'block' }} alt="" />
      <div style={{ flex: 1, padding: '12px 14px' }}>
        <p style={{ margin: '0 0 2px', fontWeight: 'bold', fontSize: '13px', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{libroInfo.titulo}</p>
        <p style={{ margin: '0 0 8px', fontSize: '11px', color: '#555' }}>{libroInfo.autor}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#1DB954', boxShadow: '0 0 5px #1DB954' }} />
          <span style={{ fontSize: '11px', color: '#1DB954', fontWeight: 'bold' }}>Sala activa</span>
          {sala.numMiembros > 1 && <span style={{ fontSize: '11px', color: '#333' }}>· {sala.numMiembros} lectores</span>}
        </div>
      </div>
      <div style={{ padding: '0 14px', display: 'flex', alignItems: 'center' }}>
        <span style={{ color: '#333', fontSize: '20px', fontWeight: 'bold' }}>›</span>
      </div>
    </div>
  );
}

// ── Feed principal ────────────────────────────────────────────────────────────
// ── Para ti — recomendaciones personalizadas (lógica en cliente) ─────────────
function ParaTiSection({ userId, perfil, tieneGeneros, onVerLibro, onEntrarSala }) {
  const [libros, setLibros] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const cargar = async () => {
      try {
        // 1. Libros ya vistos por el usuario
        const estadosSnap = await getDocs(
          query(collection(db, 'estados_lectura'), where('userId', '==', userId), limit(200))
        );
        const librosYaVistos = new Set(estadosSnap.docs.map(d => d.data().libroId).filter(Boolean));

        let candidatos = [];

        // 2. Libros valorados >= 4 estrellas por otros usuarios
        const valSnap = await getDocs(
          query(collection(db, 'valoraciones'), where('estrellas', '>=', 4), orderBy('estrellas', 'desc'), limit(100))
        );
        const vistos = new Set();
        valSnap.docs.forEach(d => {
          const data = d.data();
          if (!data.libroId || !data.titulo || librosYaVistos.has(data.libroId)) return;
          if (data.userId === userId) return;
          if (vistos.has(data.libroId)) return;
          vistos.add(data.libroId);
          candidatos.push({ libroId: data.libroId, id: data.libroId, titulo: data.titulo, autor: data.autor || '', portada: data.portada || '', descripcion: '', score: data.estrellas || 4 });
        });

        // 3. Si hay pocos, añadir salas activas
        if (candidatos.length < 10) {
          const salasSnap = await getDocs(
            query(collection(db, 'salas_info'), where('activa', '==', true), limit(30))
          );
          salasSnap.docs.forEach(d => {
            const data = d.data();
            if (!data.libroId || !data.titulo || librosYaVistos.has(data.libroId)) return;
            if (candidatos.find(c => c.libroId === data.libroId)) return;
            candidatos.push({ libroId: data.libroId, id: data.libroId, titulo: data.titulo, autor: data.autor || '', portada: data.portada || '', descripcion: '', score: 3 });
          });
        }

        // 4. Ordenar y tomar top 15
        candidatos = candidatos.sort((a, b) => b.score - a.score).slice(0, 15);

        // 5. Buscar portadas faltantes usando caché de Firestore
        for (let i = 0; i < candidatos.length; i++) {
          if (candidatos[i].portada?.startsWith('https://')) continue;
          const datos = await buscarPortadaConCache(candidatos[i].titulo, candidatos[i].autor);
          if (datos?.portada) candidatos[i].portada = datos.portada;
        }

        const conPortada = candidatos.filter(l => l.portada);
        if (conPortada.length > 0) {
          setLibros(conPortada);
          setCargando(false);
          return;
        }
      } catch { }
      setCargando(false);
    };
    cargar();
  }, [userId]);

  if (cargando) return (
    <div className="feed-section">
      <div style={{ height: '22px', width: '80px', background: '#141414', borderRadius: '6px', marginBottom: '16px', animation: 'pulse 1.5s infinite' }} />
      <div style={{ display: 'flex', gap: '12px', overflowX: 'auto' }}>
        {[...Array(4)].map((_, i) => (
          <div key={i} style={{ flexShrink: 0, width: '110px' }}>
            <div style={{ width: '110px', aspectRatio: '2/3', background: '#141414', borderRadius: '6px', animation: 'pulse 1.5s infinite' }} />
          </div>
        ))}
      </div>
    </div>
  );

  if (!libros.length) return null;

  const subtitulo = tieneGeneros && perfil?.generos?.length > 0
    ? `Basado en tus géneros: ${perfil.generos.slice(0, 3).join(', ')}`
    : 'Libros que están leyendo en Leemos';

  return (
    <div className="feed-section">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '6px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 900, margin: 0, color: 'white', letterSpacing: '-0.3px' }}>Para ti</h2>
      </div>
      <p style={{ color: '#444', fontSize: '13px', margin: '0 0 16px' }}>{subtitulo}</p>
      <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
        {libros.map((libro, i) => (
          <div key={i} style={{ flexShrink: 0, width: window.innerWidth < 768 ? '110px' : '130px' }}>
            <div className="book-card" style={{ padding: 0, marginBottom: '8px' }} onClick={() => onVerLibro({ ...libro, id: libro.id || libro.libroId || generarIdLibro(libro.titulo, libro.autor), descripcion: libro.descripcion || '' })}>
              <img src={libro.portada} alt=""
                style={{ width: '100%', aspectRatio: '2/3', objectFit: 'cover', borderRadius: '6px', display: 'block' }}
                onError={e => e.currentTarget.style.display = 'none'} />
            </div>
            <p style={{ margin: '0 0 2px', fontWeight: 'bold', fontSize: '12px', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{libro.titulo}</p>
            <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{libro.autor}</p>
            <button onClick={() => onEntrarSala({ ...libro, id: libro.id || libro.libroId || generarIdLibro(libro.titulo, libro.autor), descripcion: libro.descripcion || '' }, false)}
              style={{ background: 'transparent', color: '#1DB954', border: '1px solid #1DB95444', borderRadius: '12px', padding: '4px 10px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}
              onMouseEnter={e => e.currentTarget.style.background = '#1DB95418'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              Ver sala
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Actividad reciente con Ver todas ─────────────────────────────────────────
function ActividadRecienteSeccion({ actividad, cargando, onVerLibro, onVerPerfil }) {
  const [verTodas, setVerTodas] = useState(false);
  const LIMITE = 5;
  const items = verTodas ? actividad : actividad.slice(0, LIMITE);
  const hayMas = actividad.length > LIMITE;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 900, margin: 0, color: 'white' }}>Actividad reciente</h2>
        {hayMas && (
          <button onClick={() => setVerTodas(!verTodas)}
            style={{ background: 'none', border: 'none', color: '#1DB954', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {verTodas ? 'Ver menos' : 'Ver todas'} <span style={{ fontSize: '16px' }}>›</span>
          </button>
        )}
      </div>
      {cargando
        ? [...Array(3)].map((_, i) => <div key={i} style={{ height: '62px', background: '#141414', borderRadius: '6px', animation: 'pulse 1.5s infinite', marginBottom: '4px' }} />)
        : actividad.length > 0
          ? items.map(item => (
            <ActividadRow key={item.id} item={item}
              onVerLibro={item => onVerLibro({ id: item.libroId, titulo: item.titulo, autor: item.autor, portada: item.portada, descripcion: '' })}
              onVerPerfil={item => onVerPerfil({ id: item.userId, nombre: item.usuarioNombre, foto: item.usuarioFoto })} />
          ))
          : <p style={{ color: '#333', fontSize: '13px', padding: '8px 10px' }}>Las personas que sigues aún no tienen actividad reciente.</p>
      }
    </>
  );
}

export function SeccionFeed({ userId, perfil, siguiendoA, onVerLibro, onVerPerfil, onEntrarSala, onIrEventos, onExplorar, onToggleSeguir, onIrPerfil, esPremium, onUpgrade, onIrBibliotecaLeyendo, onIrClubs, entradasCache = [] }) {
  const isMobile = window.innerWidth < 768;
  const [eventos, setEventos] = useState([]);
  const [actividad, setActividad] = useState([]);
  const [salasActivas, setSalasActivas] = useState([]);
  const [librosInfo, setLibrosInfo] = useState({});
  const [lectoresSimilares, setLectoresSimilares] = useState([]);
  const [topLectores, setTopLectores] = useState([]);
  const [cargandoActividad, setCargandoActividad] = useState(true);
  const [librosLeyendo, setLibrosLeyendo] = useState([]);
  const [statsAnio, setStatsAnio] = useState({ leidos: 0, horas: 0, retosCompletados: 0, media: 0 });
  const tieneSeguidos = siguiendoA.length > 0;
  const tieneGeneros = perfil?.generos?.length > 0;

  // Cargar libros leyendo y stats del año
  useEffect(() => {
    if (!userId) return;
    const cargar = async () => {
      try {
        // Si tenemos cache de biblioteca, usarlo directamente
        if (entradasCache.length > 0) {
          setLibrosLeyendo(entradasCache.filter(e => e.estado === 'leyendo' && e.libro?.titulo));
          const leidosAnio = entradasCache.filter(e => e.estado === 'leido').length;

          // Stats de tiempo y retos completados
        const [statsSnap, retosSnap, retoActualSnap] = await Promise.all([
          getDocs(query(collection(db, 'stats_timer'), where('__name__', '==', userId))),
          getDocs(query(collection(db, 'retos_historial'), where('userId', '==', userId))),
          getDocs(query(collection(db, 'retos'), where('userId', '==', userId))),
        ]);
        const horas = statsSnap.docs[0]?.data()?.totalSegundos ? Math.round(statsSnap.docs[0].data().totalSegundos / 3600) : 0;
        // Sumar retos del historial + reto actual si está completado
        const retoActual = retoActualSnap.docs[0]?.data();
        const retoActualCompletado = retoActual && retoActual.objetivo && (() => {
          const base = retoActual.baseLeidos || 0;
          const progreso = leidosAnio - base;
          if (base > 0 && base === leidosAnio) return false;
          return progreso >= retoActual.objetivo;
        })() ? 1 : 0;
        const retosCompletados = retosSnap.docs.length + retoActualCompletado;
        setStatsAnio({ leidos: leidosAnio, horas, retosCompletados, media: 0 });
        return;
        }

        // Sin cache — query directa (puede fallar sin índice)
        const leyendoSnap = await getDocs(query(collection(db, 'estados_lectura'), where('userId', '==', userId), where('estado', '==', 'leyendo'), limit(10)));
        setLibrosLeyendo(leyendoSnap.docs.map(d => d.data()).filter(d => d.libro?.titulo));

        const leidosSnap = await getDocs(query(collection(db, 'estados_lectura'), where('userId', '==', userId), where('estado', '==', 'leido')));
        const leidosAnio = leidosSnap.docs.length;

        const [statsSnap, retosSnap, retoActualSnap] = await Promise.all([
          getDocs(query(collection(db, 'stats_timer'), where('__name__', '==', userId))),
          getDocs(query(collection(db, 'retos_historial'), where('userId', '==', userId))),
          getDocs(query(collection(db, 'retos'), where('userId', '==', userId))),
        ]);
        const horas = statsSnap.docs[0]?.data()?.totalSegundos ? Math.round(statsSnap.docs[0].data().totalSegundos / 3600) : 0;
        const retoActual = retoActualSnap.docs[0]?.data();
        const retoActualCompletado = retoActual && retoActual.objetivo && (() => {
          const base = retoActual.baseLeidos || 0;
          const progreso = leidosAnio - base;
          if (base > 0 && base === leidosAnio) return false;
          return progreso >= retoActual.objetivo;
        })() ? 1 : 0;
        const retosCompletados = retosSnap.docs.length + retoActualCompletado;
        setStatsAnio({ leidos: leidosAnio, horas, retosCompletados, media: 0 });
      } catch (e) { console.error('Feed stats error:', e); }
    };
    cargar();
  }, [userId, entradasCache.length]);

  // Eventos
  useEffect(() => {
    if (!userId) return;
    const unsub = onSnapshot(
      query(collection(db, "eventos"), where("activo", "==", true), orderBy("fechaInicio", "asc"), limit(5)),
      snap => setEventos(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return () => unsub();
  }, [userId]);

  // Salas activas
  useEffect(() => {
    if (!userId) return;
    const unsub = onSnapshot(
      query(collection(db, "salas_info"), where("activa", "==", true), where("tipo", "==", "publica"), orderBy("ultimaActividad", "desc"), limit(8)),
      async snap => {
        const salas = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSalasActivas(salas);
        const info = {};
        for (const sala of salas) {
          if (sala.libroId && !info[sala.libroId]) {
            const actSnap = await getDocs(query(collection(db, "actividad_reciente"), where("libroId", "==", sala.libroId), limit(1)));
            if (!actSnap.empty) {
              const d = actSnap.docs[0].data();
              info[sala.libroId] = { id: sala.libroId, titulo: d.titulo, autor: d.autor, portada: d.portada, descripcion: '' };
            }
          }
        }
        setLibrosInfo(info);
      }
    );
    return () => unsub();
  }, [userId]);

  // Lectores similares
  useEffect(() => {
    if (!perfil?.generos?.length) return;
    const buscar = async () => {
      try {
        const snap = await getDocs(query(collection(db, "users"), where("generos", "array-contains-any", perfil.generos.slice(0, 10)), limit(30)));
        setLectoresSimilares(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.id !== userId && !siguiendoA.includes(u.id) && u.generos?.length > 0).slice(0, 5));
      } catch (e) { console.error(e); }
    };
    buscar();
  }, [perfil?.generos, userId, siguiendoA]);

  // Top lectores
  useEffect(() => {
    const buscar = async () => {
      try {
        const snap = await getDocs(query(collection(db, "users"), orderBy("seguidores", "desc"), limit(10)));
        setTopLectores(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.id !== userId && !siguiendoA.includes(u.id)).slice(0, 5));
      } catch (e) { console.error(e); }
    };
    buscar();
  }, [userId, siguiendoA]);

  // Actividad de seguidos
  useEffect(() => {
    if (!tieneSeguidos) { setCargandoActividad(false); return; }
    const cargar = async () => {
      setCargandoActividad(true);
      try {
        const ids = siguiendoA.slice(0, 10);
        const todas = [];
        for (const seguidoId of ids) {
          const [actSnap, estSnap, userDoc] = await Promise.all([
            getDocs(query(collection(db, "actividad_reciente"), where("userId", "==", seguidoId), orderBy("fecha", "desc"), limit(2))),
            getDocs(query(collection(db, "estados_lectura"), where("userId", "==", seguidoId), orderBy("fecha", "desc"), limit(2))),
            getDoc(doc(db, "users", seguidoId)),
          ]);
          const u = userDoc.exists() ? userDoc.data() : { nombre: 'Usuario', foto: null, marco: null };
          actSnap.docs.forEach(d => {
            const data = d.data();
            todas.push({
              id: d.id, userId: seguidoId, usuarioNombre: u.nombre, usuarioFoto: u.foto, usuarioMarco: u.marco || null,
              titulo: data.titulo, autor: data.autor, portada: data.portada,
              libroId: data.libroId, fecha: data.fecha, estado: null,
              tipo: data.tipo || null,
              reseña: data.reseña || null,
              estrellas: data.estrellas || null,
            });
          });
          estSnap.docs.forEach(d => { const data = d.data(); if (data.libro && data.estado) todas.push({ id: `e_${d.id}`, userId: seguidoId, usuarioNombre: u.nombre, usuarioFoto: u.foto, usuarioMarco: u.marco || null, titulo: data.libro.titulo, autor: data.libro.autor, portada: data.libro.portada, libroId: data.libroId, fecha: data.fecha, estado: data.estado }); });
        }
        todas.sort((a, b) => (b.fecha?.toDate?.() || new Date(0)) - (a.fecha?.toDate?.() || new Date(0)));
        const vistos = new Set();
        setActividad(todas.filter(a => { const k = `${a.userId}_${a.libroId}_${a.estado}_${a.tipo || ''}`; if (vistos.has(k)) return false; vistos.add(k); return true; }).slice(0, 15));
      } catch (e) { console.error(e); }
      setCargandoActividad(false);
    };
    cargar();
  }, [siguiendoA, tieneSeguidos]);

  useEffect(() => { if (!tieneSeguidos) setCargandoActividad(false); }, [tieneSeguidos]);

  const enVivo = eventos.filter(e => (e.fechaInicio?.toDate?.().getTime() || 0) <= Date.now());
  const proximos = eventos.filter(e => (e.fechaInicio?.toDate?.().getTime() || 0) > Date.now());
  const salasConInfo = salasActivas.filter(s => librosInfo[s.libroId]);

  const irSala = (libro) => onEntrarSala(libro, false);
  const irEvento = (ev) => onEntrarSala(
    { id: ev.libroId, titulo: ev.libroTitulo, autor: ev.libroAutor, portada: ev.libroPortada, descripcion: '' },
    false, `evento_${ev.id}`, null, ev.titulo
  );

  return (
    <div style={{ padding: isMobile ? '0 16px 60px' : '0 32px 80px', maxWidth: '800px' }}>
      <style>{`
        .feed-section { margin-bottom: 40px; }
        .salas-scroll { display: flex; gap: 12px; overflow-x: auto; padding-bottom: 8px; }
      `}</style>

      {/* Header */}
      <div style={{ padding: isMobile ? '16px 0 10px' : '28px 0 20px' }}>
        <h1 style={{ fontSize: isMobile ? '30px' : '36px', fontWeight: 900, margin: '0 0 4px', color: 'white', letterSpacing: '-0.5px' }}>
          {tieneSeguidos ? 'Tu feed' : 'Bienvenido a Leemos'}
        </h1>
        <p style={{ color: '#4a5d50', margin: 0, fontSize: '14px' }}>
          {tieneSeguidos ? `${siguiendoA.length} ${siguiendoA.length === 1 ? 'lector que sigues' : 'lectores que sigues'}` : 'Descubre qué está pasando ahora mismo'}
        </p>
      </div>

      {/* Reto activo */}
      <div className="feed-section">
        <RetoLector userId={userId} enFeed />
      </div>

      {/* Tu progreso este año */}
      <div className="feed-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <h3 style={{ fontSize: '17px', fontWeight: 900, margin: 0, color: 'white', letterSpacing: '-0.2px' }}>Tu progreso este año</h3>
        </div>
        <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {[
            { valor: statsAnio.leidos, label: 'Libros leídos', color: '#1DB954', bg: 'radial-gradient(circle at 30% 20%, #1DB95435, #0a1422)', iconPath: 'M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z' },
            { valor: `${statsAnio.horas}h`, label: 'Horas de lectura', color: '#a78bfa', bg: 'radial-gradient(circle at 30% 20%, #a78bfa35, #0e1124)', iconPath: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z' },
            { valor: `${statsAnio.retosCompletados}`, label: 'Retos completados', color: '#FFD700', bg: 'radial-gradient(circle at 30% 20%, #FFD70035, #1f1a0a)', iconPath: 'M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94A5.01 5.01 0 0 0 11 15.9V18H9v2h6v-2h-2v-2.1a5.01 5.01 0 0 0 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z' },
          ].map(({ valor, label, color, bg, iconPath }) => (
            <div key={label} style={{ flex: '1 1 0', minWidth: '96px', background: bg, borderRadius: '18px', border: `1px solid ${color}2a`, padding: '20px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: `${color}20`, border: `1px solid ${color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 16px ${color}40` }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill={color}><path d={iconPath}/></svg>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '30px', fontWeight: 900, color, lineHeight: 1, letterSpacing: '-0.5px' }}>{valor}</p>
                <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#5a6b5f', lineHeight: 1.3, fontWeight: 600 }}>{label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Descubre Clubs — banner llamativo */}
      {onIrClubs && (
        <div className="feed-section">
          <button onClick={onIrClubs} style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
            background: 'linear-gradient(135deg, #0d2613, #0a1c0f)', border: '1px solid #1DB95433',
            borderRadius: '16px', padding: '14px 18px', cursor: 'pointer', transition: '0.2s',
            textAlign: 'left',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#1DB95466'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#1DB95433'; e.currentTarget.style.transform = 'translateY(0)'; }}>
            <span style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#1DB95425', border: '1px solid #1DB95455', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#1DB954"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
            </span>
            <span style={{ flex: 1, fontSize: '13px', color: '#cde8d3', fontWeight: 600 }}>
              Descubre los mejores libros del momento en <span style={{ color: '#1DB954', fontWeight: 900 }}>Clubs</span>
            </span>
            <span style={{ color: '#1DB954', fontSize: '18px' }}>›</span>
          </button>
        </div>
      )}

      {/* Wrap mensual compartible */}
      <div className="feed-section">
        <WrapMensual userId={userId} nombre={perfil?.nombre} />
      </div>

      {/* Buscar por vibe */}
      <div className="feed-section" style={{ background: '#111', borderRadius: '14px', padding: '16px', border: '1px solid #1e1e1e' }}>
        <BuscarPorVibe onVerLibro={onVerLibro} />
      </div>

      {/* Continúa tu lectura */}
      {librosLeyendo.length > 0 && (
        <div className="feed-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 900, margin: 0, color: 'white' }}>Continúa tu lectura</h3>
            {onIrBibliotecaLeyendo && (
              <button onClick={() => onIrBibliotecaLeyendo(null)}
                style={{ background: 'none', border: 'none', color: '#1DB954', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Ver todos <span style={{ fontSize: '16px' }}>›</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: '4px' }}>
            {librosLeyendo.map(entrada => {
              const pct = entrada.paginaActual && entrada.totalPaginas ? Math.min(Math.round((entrada.paginaActual / entrada.totalPaginas) * 100), 100) : null;
              return (
                <div key={entrada.libroId} style={{ flexShrink: 0, width: '130px' }}>
                  {/* Portada — click abre panel biblioteca */}
                  <div onClick={() => onIrBibliotecaLeyendo ? onIrBibliotecaLeyendo(entrada) : onVerLibro(entrada.libro)}
                    style={{ position: 'relative', width: '130px', height: '195px', borderRadius: '10px', overflow: 'hidden', background: '#141414', marginBottom: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.4)', cursor: 'pointer', transition: 'transform 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                    {entrada.libro?.portada?.startsWith('https://')
                      ? <img src={entrada.libro.portada} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => e.currentTarget.style.display='none'} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>📖</div>
                    }
                    {/* Barra de progreso overlay */}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px 8px 8px', background: 'linear-gradient(to top, rgba(0,0,0,0.95), transparent)' }}>
                      <div style={{ height: '3px', background: 'rgba(255,255,255,0.12)', borderRadius: '3px', overflow: 'hidden', marginBottom: '4px' }}>
                        <div style={{ height: '100%', width: `${pct || 0}%`, background: 'linear-gradient(90deg, #1DB954, #4ade80)', borderRadius: '3px' }} />
                      </div>
                      <p style={{ margin: 0, fontSize: '10px', color: '#1DB954', fontWeight: 'bold' }}>{pct !== null ? `${pct}%` : '—'}</p>
                    </div>
                  </div>
                  <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 'bold', color: 'white', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{entrada.libro?.titulo}</p>
                  <p style={{ margin: 0, fontSize: '10px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entrada.libro?.autor}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* EN VIVO */}
      {enVivo.length > 0 && (
        <div className="feed-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f44336', boxShadow: '0 0 8px #f44336', animation: 'pulse 1.5s infinite' }} />
            <h2 style={{ fontSize: '22px', fontWeight: 900, margin: 0, color: 'white' }}>En vivo ahora</h2>
            <button onClick={onIrEventos} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#555', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}
              onMouseEnter={e => e.currentTarget.style.color = 'white'}
              onMouseLeave={e => e.currentTarget.style.color = '#555'}>
              Ver todo
            </button>
          </div>
          {enVivo.map(e => <EventoRow key={e.id} evento={e} userId={userId} onEntrar={irEvento} />)}
        </div>
      )}

      {/* Actividad de seguidos */}
      {tieneSeguidos && (
        <div className="feed-section">
          <ActividadRecienteSeccion
            actividad={actividad}
            cargando={cargandoActividad}
            onVerLibro={onVerLibro}
            onVerPerfil={onVerPerfil}
          />
        </div>
      )}



      {/* Para ti — recomendaciones personalizadas via Cloud Function */}
      <ParaTiSection
        userId={userId}
        perfil={perfil}
        tieneGeneros={tieneGeneros}
        onVerLibro={onVerLibro}
        onEntrarSala={onEntrarSala}
      />

      {/* Tendencias */}
      <div className="feed-section">
        <SeccionTitulo texto="Tendencias" accion="Ver todo" onAccion={onExplorar} />
        <TendenciasCarrusel onVerLibro={onVerLibro} onEntrarSala={onEntrarSala} />
      </div>

      {/* Descubrimiento por similitud */}
      <DescubrimientoSimilitud userId={userId} onVerLibro={onVerLibro} onEntrarSala={onEntrarSala} />

      {/* Salas activas */}
      {salasConInfo.length > 0 && (
        <div className="feed-section">
          <SeccionTitulo texto="Salas activas" />
          {salasConInfo.map(sala => (
            <SalaCard key={sala.id} sala={sala} libroInfo={librosInfo[sala.libroId]} onEntrar={irSala} />
          ))}
        </div>
      )}

      {/* Próximos eventos */}
      {proximos.length > 0 && (
        <div className="feed-section">
          <SeccionTitulo texto="Próximos eventos" accion="Ver todos" onAccion={onIrEventos} />
          {proximos.map(e => <EventoRow key={e.id} evento={e} userId={userId} onEntrar={irEvento} />)}
        </div>
      )}

      {/* Lectores similares */}
      {lectoresSimilares.length > 0 && (
        <div className="feed-section">
          <SeccionTitulo texto="Lectores como tú" accion="Buscar más" onAccion={() => onVerPerfil(null)} />
          <p style={{ color: '#444', fontSize: '13px', margin: '-10px 0 16px' }}>Comparten tus géneros favoritos</p>
          {lectoresSimilares.map(u => (
            <LectorRow key={u.id} usuario={u} onVer={onVerPerfil} onSeguir={onToggleSeguir} yaSigo={siguiendoA.includes(u.id)} />
          ))}
        </div>
      )}



      {/* CTA explorar — siempre al final */}
      <div style={{ background: 'linear-gradient(135deg, #0a1a0f, #0c1322)', borderRadius: '16px', padding: window.innerWidth < 768 ? '20px' : '28px 32px', border: '1px solid #1DB95422', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexDirection: window.innerWidth < 768 ? 'column' : 'row', textAlign: window.innerWidth < 768 ? 'center' : 'left' }}>
        <div>
          <p style={{ margin: '0 0 4px', fontWeight: 900, fontSize: '18px', color: 'white' }}>Explora más libros</p>
          <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>Cientos de títulos esperándote</p>
        </div>
        <button onClick={onExplorar} style={{ background: '#1DB954', color: 'black', border: 'none', borderRadius: '20px', padding: '12px 24px', fontWeight: 900, cursor: 'pointer', fontSize: '14px', flexShrink: 0, transition: '0.2s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => e.currentTarget.style.background = '#1ed760'}
          onMouseLeave={e => e.currentTarget.style.background = '#1DB954'}>
          Explorar
        </button>
      </div>
    </div>
  );
}
